
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { QRCodeModal } from "@/components/tickets/QRCodeModal";
import { Share, QrCode, Link, CopyCheck } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from "@/components/ui/dialog";

interface ProfileSharingProps {
  username: string;
}

export const ProfileSharing = ({ username }: ProfileSharingProps) => {
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();
  
  const profileUrl = `${window.location.origin}/profile/${username}`;
  
  const copyProfileLink = () => {
    navigator.clipboard.writeText(profileUrl);
    setCopied(true);
    
    toast({
      title: "Link Copied",
      description: "Profile link copied to clipboard",
    });
    
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <>
      <Button 
        variant="outline"
        onClick={() => setShareDialogOpen(true)}
        className="flex items-center gap-2"
      >
        <Share className="h-4 w-4" />
        Share Profile
      </Button>
      
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Share Profile</DialogTitle>
            <DialogDescription>
              Share your profile with others using a QR code or link
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex flex-col gap-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex-1 mr-4">
                <p className="text-sm font-medium mb-1">Profile Link</p>
                <div className="flex items-center gap-2">
                  <input
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                    value={profileUrl}
                    readOnly
                  />
                </div>
              </div>
              
              <Button 
                size="sm" 
                onClick={copyProfileLink}
                variant={copied ? "default" : "secondary"}
              >
                {copied ? (
                  <>
                    <CopyCheck className="h-4 w-4 mr-2" />
                    Copied
                  </>
                ) : (
                  <>
                    <Link className="h-4 w-4 mr-2" />
                    Copy
                  </>
                )}
              </Button>
            </div>
            
            <div className="flex flex-col items-center justify-center p-4">
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => {
                  setShareDialogOpen(false);
                  setQrModalOpen(true);
                }}
              >
                <QrCode className="h-4 w-4 mr-2" />
                Show QR Code
              </Button>
              <p className="text-sm text-muted-foreground mt-2">
                People can scan this code to view your profile
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      <QRCodeModal 
        isOpen={qrModalOpen} 
        onClose={() => setQrModalOpen(false)} 
        ticketId={profileUrl} 
      />
    </>
  );
};
